import { CheckIcon, WarningCircleIcon } from '@phosphor-icons/react';
import { useId, type InputHTMLAttributes, type ReactNode } from 'react';

import styles from './Checkbox.module.css';

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'className'> {
  /** Может содержать ссылки, поэтому ReactNode, а не строка. */
  children: ReactNode;
  error?: string | undefined;
  className?: string | undefined;
}

/**
 * Чекбокс с произвольной подписью.
 *
 * Настоящий `<input type="checkbox">` остаётся в DOM и принимает фокус —
 * рисуется только его визуальная замена. Клавиатура, скринридеры и
 * автозаполнение работают штатно.
 *
 * Ошибка — как у `Field`: бордовая рамка флажка плюс текст с иконкой,
 * никогда только цвет.
 */
export function Checkbox({ children, error, className, id, ...rest }: CheckboxProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const errorId = `${fieldId}-error`;

  return (
    <div className={[styles.wrapper, className].filter(Boolean).join(' ')}>
      <div className={styles.row}>
        <input
          id={fieldId}
          type="checkbox"
          className={styles.input}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          {...rest}
        />
        <span className={[styles.box, error && styles.boxError].filter(Boolean).join(' ')}>
          <CheckIcon size={13} weight="bold" aria-hidden="true" />
        </span>
        <label className={styles.label} htmlFor={fieldId}>
          {children}
        </label>
      </div>

      {error ? (
        <p id={errorId} className={styles.error} role="alert">
          <WarningCircleIcon size={16} aria-hidden="true" />
          {error}
        </p>
      ) : null}
    </div>
  );
}
